import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import EmberRouter from '@embroider/router';
import { tracked } from '@glimmer/tracking';
import ENV from 'lumen/config/environment';

export default class Router extends EmberRouter {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare version: Services['version'];

  @tracked lastURLUsageAnalytics: string | null = null;

  lastUpdateTime = new Date().getTime();
  location = ENV.locationType;
  // time in seconds since epoch
  redirectCounter = 0;
  rootURL = ENV.rootURL;

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    this.on('routeWillChange', (transition: Transition) => {
      const now = new Date().getTime();

      if (now - this.lastUpdateTime < 200) {
        // Count redirects that happen too fast, too frequent.
        // Redirect to error page when we detect an endless loop
        this.redirectCounter++;

        if (this.redirectCounter >= 50) {
          transition.abort();

          const targetName = transition.targetName!.includes('hotel')
            ? 'hotel.error'
            : 'market.error';

          this.router.transitionTo(
            targetName,
            this.hotelAndMarketService.hotelOrMarket,
            {
              queryParams: { reason: 'ERR_TOO_MANY_REDIRECTS' },
            },
          );
        }
      } else {
        this.lastUpdateTime = now;
        this.redirectCounter = 0;
      }
    });

    // We define this hook in the init because otherwise the url on the Router is wrong (not the updated url)
    // For some reason defining the hook like this fixes that problem
    // https://github.com/emberjs/ember.js/issues/16389
    this.on('routeDidChange', (transition: Transition) => {
      const willTransitionToDifferentRoute =
        !!transition.from &&
        !!transition.to &&
        transition.from.name !== transition.to.name;

      const isAppLoadTransition = transition.from === null;

      if (isAppLoadTransition) {
        // Initialise the session replay after you successfully land on your route after the loading route
        this.usageAnalytics.initialiseSessionReplay();
      }

      // Check the version when changing routes or subroutes, so not when query parameters change on the same route
      // Skip when entering the app for the first time
      if (willTransitionToDifferentRoute) {
        this.version.manualPoll();
        this.usageAnalytics.initialiseSessionReplay();
      }

      /*
       * Segment usage tracking
       * We track the page view on every route change
       * Even on query parameter changes, when the route stays the same
       * We do this since data analytics team uses this to track user activity
       * Note: because of the transition service we sometimes get the exact same route multiple times so added a check to prevent duplicate events
       * When we come from application_loading the url is the same so do make sure we track transitions between different routes
       */
      if (
        willTransitionToDifferentRoute ||
        this.lastURLUsageAnalytics !== this.url
      ) {
        this.usageAnalytics.page();
        this.lastURLUsageAnalytics = this.url;
      }

      this.intercom.update(transition.to?.name);
    });
  }
}

Router.map(function () {
  this.route('destination', { path: '/daas/:id' }, function () {
    this.route('error');
    this.route('reporting', function () {
      this.route('dashboard', { path: '/dashboard/:dashboard_id' });
      this.route('scheduled-reports');
    });
    this.route('settings', function () {
      this.route('hotels', function () {
        this.route('edit');
      });
      this.route('personal');
      this.route('reports');
      this.route('security');
      this.route('theme');
    });
  });
  this.route('hotel', { path: '/hotel/:id' }, function () {
    this.route('accounts');
    this.route('benchmark-insight', function () {
      this.route('desired-compset');
      this.route('market-composition', { path: '/data-composition-report' });
      this.route('onboarding');
      this.route('terms-and-conditions');
      this.route('upsell', { path: '/preview' });
    });
    this.route('bi-upsell', { path: '/performance-preview' });
    this.route('budget');
    this.route('channel-manager');
    this.route('channel-pricing');
    this.route('day-by-day', function () {
      this.route('spider-dashboard', { path: '/:path' });
    });
    this.route('error');
    this.route('events', function () {
      this.route('edit');
    });
    this.route('account-selection');
    this.route('forecast');
    this.route('market-insight');
    this.route('market-insight-upsell', { path: '/market-insight-preview' });
    this.route('market-insight-unsupported-market');
    this.route('overview', function () {
      this.route('settings');
    });
    this.route('parity', function () {
      this.route('insight', function () {
        this.route('trends');
      });
      this.route('pos');
      this.route('list');
      this.route('overview');
    });
    this.route('portal');
    this.route('pricing-manager', function () {
      this.route('bulk-update');
      this.route('detail');
    });
    this.route('ranking');
    this.route('rates', function () {
      this.route('detail');
      this.route('export');
    });
    this.route('reservation-manager');
    this.route('revenue-insight', { path: '/revenue' }, function () {
      this.route('breakdown-detail');
      this.route('detail');
    });
    this.route('reviews');
    this.route('revpak', function () {
      this.route('spider-dashboard', { path: '/:path' });
    });
    this.route('revpak-exports', { path: '/revpak/exports' });
    this.route('segment-drilldown');
    this.route('settings', function () {
      this.route('my-hotel', function () {
        this.route('hotel-details');
        this.route('general-defaults');
      });
      this.route('autopilot');
      this.route('budget', function () {
        this.route('detail', { path: '/:entry_id' });
      });
      this.route('central-mapping', function () {
        this.route('channel-room-categories');
        this.route('hierarchy');
        this.route('mapping');
        this.route('menu');
      });
      this.route('competitors', function () {
        this.route('benchmark', function () {
          this.route('compset', function () {
            this.route('builder', function () {
              this.route('desired-compset');
              this.route('generate');
              this.route('proposals');
              this.route('review');
            });
          });
          this.route('smart-compset', function () {
            this.route('categories');
            this.route('review');
            this.route('validation');
            this.route('weights');
          });
        });
        this.route('general');
      });
      this.route('forecast', function () {
        this.route('detail', { path: '/:entry_id' });
      });
      this.route('currency-conversion', { path: '/currency' }, function () {
        this.route('profile', { path: '/:profile_id' });
      });
      this.route('hotels', function () {
        this.route('edit');
      });
      this.route('mapping', function () {
        this.route('benchmark');
        this.route('revenue');
      });
      this.route('myotb', function () {
        this.route('active');
        this.route('benchmark');
        this.route('in-progress');
        this.route('intro', function () {
          this.route('contact-admin');
          this.route('contact-support');
        });
        this.route('other-system-eligibility');
        this.route('other-system-selection');
        this.route('pms-eligibility');
        this.route('pms-selection');
        this.route('pms-setup');
        this.route('pms-unsupported');
        this.route('validation');
      });
      this.route('activity');
      this.route('personal');
      this.route('pms-integration');
      this.route('pos');
      this.route('preferred-rate');
      this.route('recommendations', function () {
        this.route('competitors-impact');
        this.route('price-range');
        this.route('rolling-reference-rates');
        this.route('room-hierarchy');
      });
      this.route('reports');
      this.route('security');
      this.route('spider', function () {
        this.route('spider-dashboard', { path: '/:path' });
      });
      this.route('taxes');
      this.route('theme');
      this.route('users');
    });
    this.route('short-term-rental');
    this.route('short-term-rental-upsell');
    this.route('snapshot', function () {
      this.route('spider-dashboard', { path: '/:path' });
    });
    this.route('strategy', function () {
      this.route('cancellation');
      this.route('dow');
      this.route('leadtime');
      this.route('los');
      this.route('mealtype');
      this.route('membership');
      this.route('mobile');
      this.route('pos');
      this.route('position');
      this.route('roomtype');
    });
    this.route('wizards', function () {
      this.route('preferred-rate-onboarding', function () {
        this.route('meal-type');
        this.route('persons');
        this.route('rate-type');
        this.route('room-type');
      });
    });
  });
  this.route('index', { path: '/' });
  this.route('market', { path: '/market/:id' }, function () {
    this.route('custom-reporting', function () {
      this.route(
        'dashboard',
        { path: '/dashboard/:dashboard_id' },
        function () {
          this.route('edit');
        },
      );
      this.route('explore', { path: '/explore/:my_model/:my_explore' });
      this.route('scheduled-reports');
      this.route('templates');
    });
    this.route('error');
    this.route('executive', function () {
      this.route('spider-dashboard', { path: '/:path' });
    });
    this.route('heatmap');
    this.route('marketrates', { path: '/rates/hotel' }, function () {
      this.route('detail');
      this.route('export');
    });
    this.route('parity', function () {
      this.route('insight', function () {
        this.route('historical');
        this.route('trends');
      });
      this.route('parity-manager', { path: '/manager' }, function () {
        this.route('addissue');
        this.route('issueupload');
        this.route('mail');
      });
      this.route('strategy');
    });
    this.route('portal');
    this.route('revenue-insight', { path: '/revenue' }, function () {
      this.route('breakdown-detail');
      this.route('currency-table');
      this.route('detail');
      this.route('issue-table');
      this.route('portfolio-details-modal');
    });
    this.route('revpak-exports', { path: 'revpak/exports' });
    this.route('segment-drilldown');
    this.route('snapshots', function () {
      this.route('overview');
    });
    this.route('settings', function () {
      this.route('central-mapping', function () {
        this.route('hierarchy');
        this.route('mapping');
        this.route('menu');
      });
      this.route('currency-conversion', { path: '/currency' }, function () {
        this.route('profile', { path: '/:profile_id' });
      });
      this.route('hotels', function () {
        this.route('edit');
      });
      this.route('personal');
      this.route('property-tags');
      this.route('reports');
      this.route('security');
      this.route('spider', function () {
        this.route('spider-dashboard', { path: '/:path' });
      });
      this.route('theme');
      this.route('transaction-mapping');
    });
    this.route('smart-insights');
    this.route('total-revenue');
  });
  this.route('spider-portfolio', { path: '/market/new' });
  this.route('start-recording');

  // Redirect to index if url is unknown
  this.route('index', { path: '/*path' });
});
